import { IPlayer, IPlayerName } from '../../../../shared/types/player.type';
import { GameStatus } from './constants';
import { v4 as uuidv4 } from 'uuid';
import { yardFields } from '../Board/Board';

const sessionId: string = uuidv4();
let tokenPositions: IPlayer[];
let lastActivePlayer: IPlayer[];
let diceNumber: number = 1;
let playerSelection: {
  isPlayerSelected: boolean[];
  selectedPlayer: number | null;
};
let move: string = '';
let observer:
  | null
  | ((
      tokenPositions: IPlayer[],
      diceNumber: number,
      playerNames: IPlayerName[],
      move: string,
      changes: {
        text: string;
        from: [number, number] | [null, null];
        to: [number, number] | [null, null];
      }[],
      gameStatus: GameStatus,
      playerSelection: {
        isPlayerSelected: boolean[];
        selectedPlayer: number | null;
      },
      activePlayer: number | null,
      stateChangeId: number | null,
    ) => void) = null;
let playerNames: IPlayerName[] = [];
let changes: {
  text: string;
  from: [number, number] | [null, null];
  to: [number, number] | [null, null];
}[] = [];
let gameStatus: GameStatus = GameStatus.LOADING;
let activePlayer: null;
let stateChangeId: null;

const PORT = process.env.REACT_APP_EXTERNAL_SERVER_PORT || 8000;
const subdomain = process.env.REACT_APP_EXTERNAL_SERVER_SUBDOMAIN || 'backend';

const hostName = window.location.hostname;
let hostNameParts = hostName.split('.');
if (hostNameParts.length > 2) {
  hostNameParts[0] = subdomain;
}
const externalHostname = hostNameParts.join('.');

function emitChange() {
  switch (ws?.readyState) {
    case 0:
      gameStatus = GameStatus.LOADING;
      break;
    case 3:
      gameStatus = GameStatus.ERROR;
      break;
    default:
      gameStatus = GameStatus.OK;
  }
  observer!(
    tokenPositions,
    diceNumber,
    playerNames,
    move,
    changes,
    gameStatus,
    playerSelection,
    activePlayer,
    stateChangeId,
  );
}

let ws: WebSocket | null;
function initWebsocket() {
  if (ws) {
    ws.onerror = ws.onopen = ws.onclose = null;
    ws.close();
  }
  ws = new WebSocket(
    `${window.location.protocol === 'https:' ? 'wss://' + externalHostname : 'ws://' + window.location.hostname + ':' + PORT}`,
  );
  ws.onopen = () => {
    console.log('Connection opened!');
    if (ws) {
      ws.send(JSON.stringify({ id: sessionId }));
    }
  };

  ws.onerror = () => {
    console.error('Connection error!');
    emitChange();
  };

  ws.onmessage = ({ data }) => {
    const {
      tokenPositions: newTokenPositions,
      diceNumber: newDiceNumber,
      playerNames: newPlayerNames,
      lastActivePlayer: newLastActivePlayer,
      move: newMove,
      changes: newChange,
      playerSelection: newPlayerSelection,
      activePlayer: newActivePlayer,
      stateChangeId: newStateChangeId,
    } = JSON.parse(data);
    // change in tokenPositions
    if (newTokenPositions !== undefined) {
      tokenPositions = newTokenPositions;
    }

    // change in lastActivePlayer
    if (newLastActivePlayer !== undefined) {
      lastActivePlayer = newLastActivePlayer;
    }

    // change in diceNumber
    if (newMove !== undefined) {
      move = newMove;
    }

    if (move === 'RESTARTGAME') {
      tokenPositions = newTokenPositions;
      playerSelection = newPlayerSelection;
      activePlayer = newActivePlayer;
      stateChangeId = newStateChangeId;
      changes = [];
      emitChange();
      return;
    }

    // change in diceNumber
    if (newDiceNumber !== undefined) {
      diceNumber = newDiceNumber;
    }

    // change in playerNames
    if (newPlayerNames !== undefined) {
      playerNames = newPlayerNames;
    }

    // change in playerSelection
    if (newPlayerSelection !== undefined) {
      playerSelection = newPlayerSelection;
    }

    if (newActivePlayer !== undefined) {
      activePlayer = newActivePlayer;
    }

    if (newStateChangeId !== undefined) {
      stateChangeId = newStateChangeId;
    }

    if (newChange !== undefined) {
      if (changes.length >= 200) {
        // limit the array to 200 entries
        changes.shift();
      }
      if (newChange.movedToken !== null) {
        // change detected
        changes.push({
          text: `\${${lastActivePlayer}} moved a figure`,
          from: newChange.from,
          to: newChange.to,
        });
      }
      if (newChange.dice) {
        diceNumber = newDiceNumber;
        changes.push({
          text: `\${${lastActivePlayer}} casted the dice. Result is: ${diceNumber}`,
          from: [null, null],
          to: [null, null],
        });
      }
    }

    emitChange();
  };
  ws.onclose = function () {
    ws = null;
  };
}

export function observe(
  o: (
    tokenPositions: IPlayer[],
    diceNumber: number,
    playerNames: IPlayerName[],
    move: string,
    changes: {
      text: string;
      from: [number, number] | [null, null];
      to: [number, number] | [null, null];
    }[],
    gameStatus: GameStatus,
    playerSelection: {
      isPlayerSelected: boolean[];
      selectedPlayer: number | null;
    },
    activePlayer: number | null,
    stateChangeId: number | null,
  ) => void,
): void {
  if (observer) {
    throw new Error('Multiple observers not implemented.');
  }
  initWebsocket();
  observer = o;
  emitChange();
}

function moveTokenToYard(player: IPlayer) {
  const playerYard = yardFields[player.number];

  if (!playerYard) {
    console.error(`No yard fields found for player ID: ${player.number}`);
    return;
  }

  for (let field of playerYard) {
    if (
      !tokenPositions.some(
        (t) => t.position[0] === field[0] && t.position[1] === field[1],
      )
    ) {
      player.position = field as [number, number];
      move = 'MOVEPLAYER';
      emitChange();
      return;
    }
  }
}

function placeToken(
  newPosition: [number, number],
  props: { tokenPositions: IPlayer[]; currentPlayerId: number },
) {
  const currentPlayer = props.tokenPositions.find(
    (currentPlayer) =>
      currentPlayer.position[0] === newPosition[0] &&
      currentPlayer.position[1] === newPosition[1],
  );

  if (currentPlayer) {
    moveTokenToYard(currentPlayer);
  }

  const currentPlayerIndex = props.tokenPositions.findIndex(
    (currentPlayer) => currentPlayer.id === props.currentPlayerId,
  );

  if (currentPlayerIndex !== -1) {
    props.tokenPositions[currentPlayerIndex].position = newPosition;
  }
}

export function canMovePlayer(
  toX: number,
  toY: number,
  isEmpty: boolean,
  isAccessible: boolean,
): boolean {
  return !isEmpty && isAccessible && toX + toY >= 0;
}

export function movePlayer(toX: number, toY: number, id?: number) {
  if (
    id === undefined ||
    playerSelection.selectedPlayer === null ||
    ![0, 1, 2, 3].includes(playerSelection.selectedPlayer)
  ) {
    console.log('ERRRRRRR');
    return;
  }
  if (playerSelection.selectedPlayer !== activePlayer) {
    console.log('NOT YOUR TURN');
    return;
  }
  placeToken([toX, toY], {
    tokenPositions: tokenPositions,
    currentPlayerId: id,
  });
  tokenPositions.forEach((player) => {
    if (player.id === id) {
      player.position[0] = toX;
      player.position[1] = toY;
    }
  });
  move = 'MOVEPLAYER';
  emitChange();
  if (ws) {
    ws.send(JSON.stringify({ id: sessionId, tokenPositions, move: move }));
  }
}

export function castDice() {
  if (
    playerSelection.selectedPlayer === null ||
    ![0, 1, 2, 3].includes(playerSelection.selectedPlayer)
  ) {
    console.log('ERRRRRRR');
    return;
  }
  if (playerSelection.selectedPlayer !== activePlayer) {
    console.log('NOT YOUR TURN');
    return;
  }
  diceNumber = Math.ceil(Math.random() * 6);
  move = 'CAST';
  if (ws) {
    ws.send(
      JSON.stringify({ id: sessionId, diceNumber: diceNumber, move: move }),
    );
  }
}

export function moveActivePlayerIndicator() {
  console.log('moved player');
  if (ws) {
    ws.send(
      JSON.stringify({ id: sessionId, move: 'MOVEACTIVEPLAYERINDICATOR' }),
    );
  }
}

export const changePlayerNames = (newPlayerNames: IPlayerName[]): void => {
  if (ws) {
    ws.send(
      JSON.stringify({
        id: sessionId,
        playerNames: newPlayerNames,
        move: 'CHANGEPLAYERNAMES',
      }),
    );
  }
};

export const changeSelectedPlayer = (newSelectedPlayer: number): void => {
  if (ws) {
    ws.send(
      JSON.stringify({
        id: sessionId,
        selectedPlayer: newSelectedPlayer,
        move: 'CHANGESELECTEDPLAYER',
      }),
    );
  }
};

export function restartGame() {
  console.log('restart game');
  if (ws) {
    ws.send(JSON.stringify({ id: sessionId, move: 'RESTARTGAME' }));
  }
}
